import axios from "axios";

// REACT_APP_BACKEND_URL may or may not already include a trailing "/api". We
// normalize it to a bare origin so we can safely append "/api/v1" exactly once —
// no matter how the env var is configured (prevents the /api/api/v1 double-prefix).
const RAW_BACKEND = (process.env.REACT_APP_BACKEND_URL || "").trim();
export const API_ORIGIN = RAW_BACKEND.replace(/\/+$/, "").replace(/\/api$/, "");
export const API_BASE = `${API_ORIGIN}/api/v1`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const orgId = localStorage.getItem("growthai_org");
  if (orgId) config.headers["X-Org-Id"] = orgId;
  return config;
});

// ---- Silent, single-flight access-token refresh (rotating refresh token) ----
// A short-lived access token is transparently renewed via POST /auth/refresh.
// One in-flight refresh is shared by all callers so a burst of 401s triggers a
// single rotation (avoids refresh storms). We use the RAW axios (not `api`) for
// the refresh call so the response interceptor cannot recurse on it.
let refreshPromise = null;

export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthFlow =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/logout");

    // On a 401 (expired access token), attempt exactly ONE silent refresh + retry.
    // `skipAuthRefresh` opts a request out (used by the initial /me probe so an
    // anonymous 401 never triggers a background refresh+logout that could race a
    // concurrent login).
    if (status === 401 && !original._retry && !original.skipAuthRefresh && !isAuthFlow) {
      original._retry = true;
      try {
        await refreshSession();
        return api(original); // retry the original request once
      } catch (e) {
        // Refresh failed (expired/revoked/reused) → force logout everywhere.
        window.dispatchEvent(new Event("auth:logout"));
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

export function apiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
